import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useLocation } from "wouter";
import { setLegalLang, useLegalLang, type LegalLang } from "@/lib/legalLocale";

function goBack(setLocation: (path: string) => void) {
  if (window.history.length > 1) {
    window.history.back();
    return;
  }
  setLocation("/");
}

export function LegalLangToggle({ className = "" }: { className?: string }) {
  const lang = useLegalLang();
  const choose = (next: LegalLang) => () => setLegalLang(next);
  return (
    <div className={`flex items-center gap-1 text-xs font-bold ${className}`}>
      <button
        type="button"
        onClick={choose("en")}
        className={lang === "en" ? "text-foreground" : "text-muted-foreground"}
        aria-pressed={lang === "en"}
      >
        EN
      </button>
      <span className="text-muted-foreground/60">/</span>
      <button
        type="button"
        onClick={choose("zh")}
        className={lang === "zh" ? "text-foreground" : "text-muted-foreground"}
        aria-pressed={lang === "zh"}
      >
        中文
      </button>
    </div>
  );
}

export function LegalPage({ title, children }: { title: string; children: ReactNode }) {
  const [, setLocation] = useLocation();
  const lang = useLegalLang();
  return (
    <div className="min-h-screen bg-background pb-4">
      <div className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-md border-b pt-safe">
        <div className="flex items-center gap-1 px-4 h-11 max-w-md mx-auto">
          <Button
            variant="ghost"
            size="icon"
            className="-ml-2 shrink-0"
            onClick={() => goBack(setLocation)}
            aria-label={lang === "en" ? "Back" : "返回"}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-bold truncate">{title}</h1>
          <LegalLangToggle className="ml-auto shrink-0" />
        </div>
      </div>
      <div className="shrink-0 pt-safe" aria-hidden>
        <div className="h-11" />
      </div>
      <div className="max-w-md mx-auto px-4 pb-10 pt-5 space-y-8 text-sm leading-relaxed text-muted-foreground">
        {children}
      </div>
    </div>
  );
}
